import axios, { InternalAxiosRequestConfig } from 'axios';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean;
}

type GetAccessTokenSilently = () => Promise<string | null>;

export let getAccessTokenSilentlyRef: GetAccessTokenSilently | null = null;

export const setGetAccessTokenSilently = (fn: GetAccessTokenSilently): void => {
	getAccessTokenSilentlyRef = fn;
};

export const refreshAccessToken = async (): Promise<string> => {
	if (!getAccessTokenSilentlyRef) {
		throw new Error('getAccessTokenSilently is not set.');
	}

	const token = await getAccessTokenSilentlyRef();
	if (!token) {
		throw new Error("Can't get token.");
	}

	return token;
};

export const apiClient = axios.create();

let isRefreshing: boolean = false;

type RequestCallback = (newToken: string) => void;

let refreshSubscribers: RequestCallback[] = [];

function subscribeTokenRefresh(callback: RequestCallback): void {
	refreshSubscribers.push(callback);
}

function onRefreshed(newToken: string): void {
	refreshSubscribers.forEach(callback => callback(newToken));
	refreshSubscribers = [];
}

apiClient.interceptors.request.use(async config => {
	const token = await getAccessTokenSilentlyRef?.();

	if (token) {
		config.headers.set('Authorization', `Bearer ${token}`);
	}

	return config;
});

apiClient.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config as CustomAxiosRequestConfig;

		const isRetryableStatus = error.response.status === 403 || error.response.status === 401;

		if (!error.response || !isRetryableStatus || originalRequest._retry) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		if (isRefreshing) {
			return new Promise(resolve => {
				subscribeTokenRefresh((newToken: string) => {
					if (originalRequest.headers) {
						originalRequest.headers.Authorization = `Bearer ${newToken}`;
					}
					resolve(apiClient(originalRequest));
				});
			});
		}

		isRefreshing = true;
		try {
			const newToken = await refreshAccessToken();

			apiClient.defaults.headers.common.Authorization = `Bearer ${newToken}`;

			onRefreshed(newToken);

			originalRequest.headers?.set('Authorization', `Bearer ${newToken}`);

			return apiClient(originalRequest);
		} catch (err) {
			return Promise.reject(err);
		} finally {
			isRefreshing = false;
		}
	}
);

apiClient.interceptors.response.use(
	res => res,
	err => {
		const { response } = err;
		if (response && typeof response.data === 'string') {
			try {
				response.data = JSON.parse(response.data);
			} catch {
				// do nothing
			}
		}
		return Promise.reject(err);
	}
);
export default apiClient;
